import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Input, Textarea, Button, Card } from '@brainsway/ui';
import type { HospitalFormData, Hospital } from '../types';

function createHospitalFormSchema(t: (key: string) => string) {
  return z.object({
    name: z.string().min(1, t('validation.nameRequired')).max(200),
    nameEn: z.string().max(200).optional().or(z.literal('')),
    code: z.string().min(1, t('validation.codeRequired')).max(20),
    city: z.string().min(1, t('validation.cityRequired')).max(100),
    address: z.string().max(300).optional().or(z.literal('')),
    contactName: z.string().max(100).optional().or(z.literal('')),
    contactPhone: z.string().max(20).optional().or(z.literal('')),
    contactEmail: z.string().email(t('validation.invalidEmail')).optional().or(z.literal('')),
    department: z.string().max(200).optional().or(z.literal('')),
    equipmentNotes: z.string().max(1000).optional().or(z.literal('')),
    notes: z.string().max(2000).optional().or(z.literal('')),
    latitude: z.coerce.number().min(-90).max(90).optional().or(z.literal('')),
    longitude: z.coerce.number().min(-180).max(180).optional().or(z.literal('')),
  });
}

type FormValues = z.output<ReturnType<typeof createHospitalFormSchema>>;

interface HospitalFormProps {
  existingHospital?: Hospital;
  onSubmit: (data: HospitalFormData) => void;
  isLoading?: boolean;
}

export function HospitalForm({ existingHospital, onSubmit, isLoading }: HospitalFormProps) {
  const { t } = useTranslation();
  const schema = createHospitalFormSchema(t);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: existingHospital?.name || '',
      nameEn: existingHospital?.nameEn || '',
      code: existingHospital?.code || '',
      city: existingHospital?.city || '',
      address: existingHospital?.address || '',
      contactName: existingHospital?.contactName || '',
      contactPhone: existingHospital?.contactPhone || '',
      contactEmail: existingHospital?.contactEmail || '',
      department: existingHospital?.department || '',
      equipmentNotes: existingHospital?.equipmentNotes || '',
      notes: existingHospital?.notes || '',
      latitude: existingHospital?.latitude ?? '',
      longitude: existingHospital?.longitude ?? '',
    },
  });

  const onFormSubmit = (data: FormValues) => {
    const cleaned: HospitalFormData = {
      name: data.name,
      nameEn: data.nameEn || undefined,
      code: data.code,
      city: data.city,
      address: data.address || undefined,
      contactName: data.contactName || undefined,
      contactPhone: data.contactPhone || undefined,
      contactEmail: data.contactEmail || undefined,
      department: data.department || undefined,
      equipmentNotes: data.equipmentNotes || undefined,
      notes: data.notes || undefined,
      latitude: typeof data.latitude === 'number' ? data.latitude : undefined,
      longitude: typeof data.longitude === 'number' ? data.longitude : undefined,
    };
    onSubmit(cleaned);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <Card>
        <div className="p-6 space-y-5">
          {/* Name, English Name & Code */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input
              label={t('hospitals.name', 'Hospital Name')}
              {...register('name')}
              error={errors.name?.message}
            />
            <Input
              label={t('hospitals.nameEn', 'Name (English)')}
              {...register('nameEn')}
              error={errors.nameEn?.message}
            />
            <Input
              label={t('hospitals.code', 'Code')}
              {...register('code')}
              error={errors.code?.message}
              placeholder="e.g. TLV-ICH"
            />
          </div>

          {/* City & Address */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label={t('hospitals.city', 'City')}
              {...register('city')}
              error={errors.city?.message}
            />
            <Input
              label={t('hospitals.address', 'Address')}
              {...register('address')}
              error={errors.address?.message}
            />
          </div>

          {/* Department */}
          <Input
            label={t('hospitals.department', 'Department')}
            {...register('department')}
            error={errors.department?.message}
          />

          {/* Contact Info */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input
              label={t('hospitals.contactName', 'Contact Name')}
              {...register('contactName')}
              error={errors.contactName?.message}
            />
            <Input
              label={t('hospitals.contactPhone', 'Contact Phone')}
              {...register('contactPhone')}
              error={errors.contactPhone?.message}
            />
            <Input
              label={t('hospitals.contactEmail', 'Contact Email')}
              type="email"
              {...register('contactEmail')}
              error={errors.contactEmail?.message}
            />
          </div>

          {/* Coordinates */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label={t('hospitals.latitude', 'Latitude')}
              type="number"
              step="any"
              {...register('latitude')}
              error={errors.latitude?.message}
              placeholder="e.g. 32.0853"
            />
            <Input
              label={t('hospitals.longitude', 'Longitude')}
              type="number"
              step="any"
              {...register('longitude')}
              error={errors.longitude?.message}
              placeholder="e.g. 34.7818"
            />
          </div>

          {/* Equipment Notes */}
          <Textarea
            label={t('hospitals.equipmentNotes', 'Equipment Notes')}
            {...register('equipmentNotes')}
            rows={2}
            error={errors.equipmentNotes?.message}
          />

          {/* Notes */}
          <Textarea
            label={t('reports.notes')}
            {...register('notes')}
            rows={3}
            error={errors.notes?.message}
          />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={isLoading}>
          {existingHospital ? t('common.save') : t('hospitals.createHospital', 'Create Hospital')}
        </Button>
      </div>
    </form>
  );
}
